import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { AuthContext } from '../contexts/AuthContext';
import AlertModal from '../components/AlertModal';
import ConfirmModal from '../components/ConfirmModal';
import ModalPortal from '../components/ModalPortal';
import PremiumSelect from '../components/PremiumSelect';
import {
  AlertState,
  Cycle,
  PayrollExpenseBucket,
  PayrollSheetDetail,
  User as UserType,
} from '../types';
import './Finance.css';
import './Payroll.css';
import '../components/ConfirmModal.css';

const BUCKET_ORDER: PayrollExpenseBucket[] = [
  'salaries',
  'withholdings',
  'thirteenth',
  'vacation',
  'other',
];

const BUCKET_LABELS: Record<PayrollExpenseBucket, string> = {
  salaries: 'Salários e pró-labore',
  withholdings: 'Encargos e recolhimentos (INSS, FGTS, IRRF patronal, etc.)',
  thirteenth: 'Décimo terceiro',
  vacation: 'Férias (+ 1/3 constitucional)',
  other: 'Outros custos de pessoal',
};

const PAYROLL_EARNING_BUCKETS: PayrollExpenseBucket[] = [
  'salaries',
  'thirteenth',
  'vacation',
  'other',
];

const PAYROLL_DISCOUNT_BUCKETS: PayrollExpenseBucket[] = ['withholdings'];

const EMPLOYEE_BUCKET_LABELS: Record<PayrollExpenseBucket, string> = {
  salaries: 'Salário / Pró-labore',
  withholdings: 'Descontos (INSS, IRRF, etc.)',
  thirteenth: 'Décimo terceiro',
  vacation: 'Férias (+ 1/3 constitucional)',
  other: 'Outras verbas',
};

const formatCurrency = (value: number): string =>
  value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

function cycleOverlapsYear(c: Cycle, y: number): boolean {
  const yearStart = Date.UTC(y, 0, 1);
  const yearEnd = Date.UTC(y, 11, 31, 23, 59, 59, 999);
  const s = new Date(c.startDate).getTime();
  const e = new Date(c.endDate).getTime();
  return s <= yearEnd && e >= yearStart;
}

/** Folha legada sem cycleId: localiza ciclo que contém o 1º dia do YYYY-MM. */
function findCycleForReferenceMonth(allCycles: Cycle[], ym: string): Cycle | null {
  const [ys, ms] = ym.split('-');
  const y = parseInt(ys, 10);
  const m = parseInt(ms, 10);
  if (!Number.isFinite(y) || !Number.isFinite(m)) return null;
  const refDay = Date.UTC(y, m - 1, 1);
  return (
    allCycles.find((c) => {
      const s = new Date(c.startDate).getTime();
      const e = new Date(c.endDate).getTime();
      return refDay >= s && refDay <= e;
    }) ?? null
  );
}

const Payroll: React.FC = () => {
  const authContext = useContext(AuthContext);
  const { user, loading: authLoading } = authContext || {};
  const canAccessPayroll = Boolean(user);
  const canManagePayroll = user?.role === 'admin' || user?.role === 'manager' || user?.role === 'hr';
  const canDownloadPayslip = user?.role === 'admin';

  const [cycles, setCycles] = useState<Cycle[]>([]);
  const [year, setYear] = useState<number>(() => new Date().getFullYear());
  const [activeCycleId, setActiveCycleId] = useState<string | null>(null);
  const [sheetsIndex, setSheetsIndex] = useState<Set<string>>(new Set());
  const [sheet, setSheet] = useState<PayrollSheetDetail | null>(null);
  const [payrollUsers, setPayrollUsers] = useState<UserType[]>([]);
  const [selectedPayrollUserId, setSelectedPayrollUserId] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [alert, setAlert] = useState<AlertState>({ isOpen: false, message: '', type: 'error' });
  const [lineDrafts, setLineDrafts] = useState<Record<PayrollExpenseBucket, { description: string; amount: string }>>(
    () =>
      Object.fromEntries(
        BUCKET_ORDER.map((b) => [b, { description: '', amount: '' }]),
      ) as Record<PayrollExpenseBucket, { description: string; amount: string }>,
  );
  const [editingLine, setEditingLine] = useState<{
    id: string;
    description: string;
    amount: string;
  } | null>(null);
  const [confirmDeleteLine, setConfirmDeleteLine] = useState<{ id: string; label: string } | null>(null);
  const [confirmDeleteSheet, setConfirmDeleteSheet] = useState<boolean>(false);
  const [createSheetModalOpen, setCreateSheetModalOpen] = useState<boolean>(false);
  const didAlignPayrollYearToActive = useRef(false);

  const cyclesForYear = useMemo(
    () =>
      cycles
        .filter((c) => cycleOverlapsYear(c, year))
        .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime()),
    [cycles, year],
  );

  const activeCycle = useMemo(
    () => cycles.find((c) => c.id === activeCycleId) ?? null,
    [cycles, activeCycleId],
  );

  const yearOptions = useMemo(() => {
    const ys = new Set<number>();
    cycles.forEach((c) => {
      ys.add(new Date(c.startDate).getFullYear());
      ys.add(new Date(c.endDate).getFullYear());
    });
    ys.add(new Date().getFullYear());
    const list = Array.from(ys).sort((a, b) => b - a);
    return list.length ? list : [new Date().getFullYear()];
  }, [cycles]);

  const yearPremiumOptions = useMemo(
    () => yearOptions.map((y) => ({ value: String(y), label: String(y) })),
    [yearOptions],
  );

  const payrollUserOptions = useMemo(
    () => [
      { value: '', label: 'Selecione o colaborador' },
      ...payrollUsers
        .filter((item) => item.active)
        .map((item) => ({ value: item.id, label: `${item.name} (${item.email})` })),
    ],
    [payrollUsers],
  );

  const cyclePremiumOptions = useMemo(
    () =>
      cyclesForYear.map((c) => ({
        value: c.id,
        label: sheetsIndex.has(c.id) ? `${c.name} (com folha)` : c.name,
      })),
    [cyclesForYear, sheetsIndex],
  );

  useEffect(() => {
    if (authLoading || !user || !canAccessPayroll) return;
    void (async () => {
      try {
        const { data } = await api.get<Cycle[]>('/cycles');
        setCycles(data);
      } catch {
        setCycles([]);
      }
    })();
  }, [authLoading, user, canAccessPayroll]);

  useEffect(() => {
    if (authLoading || !user || !canManagePayroll) return;
    void (async () => {
      try {
        const { data } = await api.get<UserType[]>('/users');
        setPayrollUsers(data);
      } catch {
        setPayrollUsers([]);
      }
    })();
  }, [authLoading, user, canManagePayroll]);

  useEffect(() => {
    if (!canManagePayroll || payrollUsers.length === 0) return;
    const activeUsers = payrollUsers.filter((item) => item.active);
    if (activeUsers.length === 0) {
      setSelectedPayrollUserId('');
      return;
    }
    if (!selectedPayrollUserId || !activeUsers.some((item) => item.id === selectedPayrollUserId)) {
      setSelectedPayrollUserId(activeUsers[0].id);
    }
  }, [canManagePayroll, payrollUsers, selectedPayrollUserId]);

  /** Na primeira carga, ajusta o ano do filtro para um que contenha o ciclo em vigor (se houver). */
  useEffect(() => {
    if (cycles.length === 0 || didAlignPayrollYearToActive.current) return;
    const globalActive = cycles.find((c) => c.active);
    if (!globalActive) {
      didAlignPayrollYearToActive.current = true;
      return;
    }
    setYear((currentYear) => {
      if (cycleOverlapsYear(globalActive, currentYear)) {
        didAlignPayrollYearToActive.current = true;
        return currentYear;
      }
      didAlignPayrollYearToActive.current = true;
      return new Date(globalActive.startDate).getUTCFullYear();
    });
  }, [cycles]);

  useEffect(() => {
    if (cyclesForYear.length === 0) {
      setActiveCycleId(null);
      return;
    }
    if (!activeCycleId || !cyclesForYear.some((c) => c.id === activeCycleId)) {
      const activeInYear = cyclesForYear.find((c) => c.active);
      setActiveCycleId(activeInYear?.id ?? cyclesForYear[0].id);
    }
  }, [cyclesForYear, activeCycleId]);

  const fetchPayrollData = useCallback(async (): Promise<void> => {
    if (!canAccessPayroll) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const listRes = await api.get<PayrollSheetDetail[]>(`/payroll/sheets?year=${year}`);
      const idx = new Set<string>();
      for (const s of listRes.data) {
        if (s.cycleId) {
          idx.add(s.cycleId);
        } else {
          const hit = findCycleForReferenceMonth(cycles, s.referenceMonth);
          if (hit) idx.add(hit.id);
        }
      }
      setSheetsIndex(idx);
      if (activeCycleId) {
        const sheetRes = await api.get<PayrollSheetDetail | null>(
          `/payroll/sheets/by-cycle/${activeCycleId}`,
        );
        setSheet(sheetRes.data);
      } else {
        setSheet(null);
      }
    } catch (err: any) {
      if (err.response?.status === 403) {
        setAlert({
          isOpen: true,
          message: 'Sem permissão para acessar a folha de pagamento.',
          type: 'error',
        });
      } else {
        setAlert({
          isOpen: true,
          message: err.response?.data?.message || 'Erro ao carregar a folha.',
          type: 'error',
        });
      }
      setSheet(null);
    } finally {
      setLoading(false);
    }
  }, [year, activeCycleId, canAccessPayroll, cycles]);

  useEffect(() => {
    if (authLoading || !user) return;
    if (!canAccessPayroll) {
      setLoading(false);
      return;
    }
    void fetchPayrollData();
  }, [authLoading, user, canAccessPayroll, fetchPayrollData]);

  const refreshAll = async (): Promise<void> => {
    await fetchPayrollData();
  };

  const previousCycleLabel = useMemo(() => {
    const sorted = [...cycles].sort(
      (a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime(),
    );
    const i = sorted.findIndex((c) => c.id === activeCycleId);
    if (i <= 0) return 'ciclo anterior';
    return sorted[i - 1].name;
  }, [cycles, activeCycleId]);

  const submitCreateSheet = async (copyFromPreviousMonth: boolean): Promise<void> => {
    if (!canManagePayroll) return;
    if (!activeCycleId) {
      setAlert({
        isOpen: true,
        message: 'Não há ciclo selecionado. Cadastre ciclos em Períodos (Ciclos).',
        type: 'error',
      });
      return;
    }
    setCreateSheetModalOpen(false);
    try {
      setSaving(true);
      const { data } = await api.post<PayrollSheetDetail>('/payroll/sheets', {
        cycleId: activeCycleId,
        copyFromPreviousMonth,
      });
      await refreshAll();
      if (copyFromPreviousMonth) {
        const n = data.copiedLinesCount ?? 0;
        if (n > 0) {
          setAlert({
            isOpen: true,
            message: `Folha criada com ${n} linha(s) copiada(s) de ${previousCycleLabel}.`,
            type: 'success',
          });
        } else {
          setAlert({
            isOpen: true,
            message:
              'Folha criada. Não havia folha ou linhas no ciclo anterior para copiar — comece em branco ou adicione linhas.',
            type: 'success',
          });
        }
      } else {
        setAlert({ isOpen: true, message: 'Folha criada em branco.', type: 'success' });
      }
    } catch (err: any) {
      setAlert({
        isOpen: true,
        message: err.response?.data?.message || 'Não foi possível criar a folha.',
        type: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleAddLine = async (bucket: PayrollExpenseBucket): Promise<void> => {
    if (!sheet || !canManagePayroll) return;
    const draft = lineDrafts[bucket];
    const amount = Number(String(draft.amount).replace(',', '.'));
    if (!selectedPayrollUserId) {
      setAlert({
        isOpen: true,
        message: 'Selecione um colaborador antes de adicionar valores.',
        type: 'error',
      });
      return;
    }
    if (!draft.description.trim() || !Number.isFinite(amount) || amount < 0) {
      setAlert({
        isOpen: true,
        message: 'Informe descrição e valor válidos.',
        type: 'error',
      });
      return;
    }
    try {
      setSaving(true);
      await api.post(`/payroll/sheets/${sheet.id}/lines`, {
        bucket,
        userId: selectedPayrollUserId,
        description: draft.description.trim(),
        amount,
      });
      setLineDrafts((prev) => ({
        ...prev,
        [bucket]: { description: '', amount: '' },
      }));
      await refreshAll();
    } catch (err: any) {
      setAlert({
        isOpen: true,
        message: err.response?.data?.message || 'Erro ao adicionar linha.',
        type: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEditLine = async (): Promise<void> => {
    if (!editingLine || !canManagePayroll) return;
    const amount = Number(String(editingLine.amount).replace(',', '.'));
    if (!editingLine.description.trim() || !Number.isFinite(amount) || amount < 0) {
      setAlert({ isOpen: true, message: 'Descrição e valor inválidos.', type: 'error' });
      return;
    }
    try {
      setSaving(true);
      await api.patch(`/payroll/lines/${editingLine.id}`, {
        description: editingLine.description.trim(),
        amount,
      });
      setEditingLine(null);
      await refreshAll();
    } catch (err: any) {
      setAlert({
        isOpen: true,
        message: err.response?.data?.message || 'Erro ao salvar linha.',
        type: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteLine = async (): Promise<void> => {
    if (!confirmDeleteLine || !canManagePayroll) return;
    try {
      setSaving(true);
      await api.delete(`/payroll/lines/${confirmDeleteLine.id}`);
      setConfirmDeleteLine(null);
      await refreshAll();
    } catch (err: any) {
      setAlert({
        isOpen: true,
        message: err.response?.data?.message || 'Erro ao excluir linha.',
        type: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSheet = async (): Promise<void> => {
    if (!sheet || !canManagePayroll) return;
    try {
      setSaving(true);
      await api.delete(`/payroll/sheets/${sheet.id}`);
      setConfirmDeleteSheet(false);
      setSheet(null);
      await fetchPayrollData();
    } catch (err: any) {
      setAlert({
        isOpen: true,
        message: err.response?.data?.message || 'Erro ao excluir folha.',
        type: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPayslip = async (lineId: string, collaboratorName: string): Promise<void> => {
    if (!canDownloadPayslip) return;
    try {
      const response = await api.get(`/payroll/lines/${lineId}/payslip`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(response.data as Blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `contracheque-${collaboratorName
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '') || 'colaborador'}.pdf`;
      anchor.click();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setAlert({
        isOpen: true,
        message: err.response?.data?.message || 'Erro ao baixar contracheque.',
        type: 'error',
      });
    }
  };

  const visiblePayrollLines = useMemo(() => {
    const lines = sheet?.lines ?? [];
    if (!canManagePayroll) {
      return user?.id ? lines.filter((line) => line.userId === user.id) : [];
    }
    if (!selectedPayrollUserId) {
      return [];
    }
    return lines.filter((line) => line.userId === selectedPayrollUserId);
  }, [canManagePayroll, selectedPayrollUserId, sheet, user?.id]);

  const visibleTotalsByBucket = useMemo(() => {
    const totals = Object.fromEntries(BUCKET_ORDER.map((b) => [b, 0])) as Record<
      PayrollExpenseBucket,
      number
    >;
    for (const line of visiblePayrollLines) {
      totals[line.bucket] += line.amount;
    }
    return totals;
  }, [visiblePayrollLines]);

  const employeeEarningsTotal = useMemo(
    () =>
      PAYROLL_EARNING_BUCKETS.reduce((sum, bucket) => sum + (visibleTotalsByBucket[bucket] ?? 0), 0),
    [visibleTotalsByBucket],
  );

  const employeeDiscountsTotal = useMemo(
    () => visibleTotalsByBucket.withholdings ?? 0,
    [visibleTotalsByBucket],
  );

  const employeeNetTotal = useMemo(
    () => employeeEarningsTotal - employeeDiscountsTotal,
    [employeeDiscountsTotal, employeeEarningsTotal],
  );

  const visibleGrandTotal = useMemo(() => {
    if (!canManagePayroll) {
      return employeeNetTotal;
    }
    return visiblePayrollLines.reduce((sum, line) => sum + line.amount, 0);
  }, [canManagePayroll, employeeNetTotal, visiblePayrollLines]);

  const selectedPayrollUser = useMemo(
    () => payrollUsers.find((item) => item.id === selectedPayrollUserId) ?? null,
    [payrollUsers, selectedPayrollUserId],
  );

  const selectedPayslipLine = useMemo(
    () => visiblePayrollLines.find((line) => line.bucket === 'salaries') ?? null,
    [visiblePayrollLines],
  );

  const linesByBucket = useCallback(
    (bucket: PayrollExpenseBucket) => visiblePayrollLines.filter((l) => l.bucket === bucket),
    [visiblePayrollLines],
  );

  const visibleBucketOrder = useMemo(
    () =>
      canManagePayroll
        ? BUCKET_ORDER
        : [...PAYROLL_EARNING_BUCKETS, ...PAYROLL_DISCOUNT_BUCKETS],
    [canManagePayroll],
  );

  return (
    <div className="finance-page">
      <section className="finance-hero">
        <div className="finance-hero-copy">
          <span className="finance-badge">
            {canManagePayroll ? 'Despesas de pessoal' : 'Meu holerite'}
          </span>
          <h1>Folha de pagamento</h1>
          <p>
            {canManagePayroll
              ? 'Controle custos por competência: salários, encargos e recolhimentos de impostos, décimo terceiro, férias (incluindo terço constitucional) e demais rubricas.'
              : 'Acompanhe seus proventos e descontos por ciclo. Salário e demais verbas aparecem em receitas; descontos em despesas.'}
          </p>
        </div>
        <div className="finance-hero-grid payroll-hero-metrics">
          <div className="finance-hero-card">
            <span>{canManagePayroll ? 'Total do período' : 'Líquido do período'}</span>
            <strong>{sheet ? formatCurrency(visibleGrandTotal) : '—'}</strong>
          </div>
          <div className="finance-hero-card">
            <span>Ciclo</span>
            <strong>{activeCycle?.name ?? '—'}</strong>
          </div>
          <div className="finance-hero-card">
            <span>Status</span>
            <strong>{sheet ? 'Folha aberta' : 'Sem folha'}</strong>
          </div>
        </div>
      </section>

      <section className="finance-surface payroll-competence-section">
        <div className="finance-section-header payroll-competence-header">
          <div className="payroll-competence-intro">
            <span className="finance-section-kicker">Competência</span>
            <h2>Ciclo de referência</h2>
            <p>
              A folha usa os mesmos períodos cadastrados em Ciclos, para bater com a totalização de
              despesas no Financeiro. Destaque verde: já existe folha naquele ciclo.
            </p>
          </div>
        </div>

        {cyclesForYear.length === 0 ? (
          <div className="payroll-competence-empty">
            <p className="payroll-no-cycles">
              Nenhum ciclo cruza este ano. Cadastre períodos em <Link to="/cycles">Ciclos</Link>.
            </p>
            <div className="payroll-toolbar premium-filters">
              <PremiumSelect
                id="payroll-year"
                label="Ano"
                wrapperClassName="premium-select-field payroll-year-select"
                value={String(year)}
                options={yearPremiumOptions}
                onChange={(v) => setYear(parseInt(v, 10))}
              />
              <PremiumSelect
                id="payroll-cycle"
                label="Ciclos deste ano"
                wrapperClassName="premium-select-field payroll-cycle-select"
                value={activeCycleId ?? ''}
                options={cyclePremiumOptions}
                onChange={(value) => {
                  setActiveCycleId(value);
                  setEditingLine(null);
                }}
              />
              {canManagePayroll ? (
                <PremiumSelect
                  id="payroll-collaborator"
                  label="Colaborador"
                  wrapperClassName="premium-select-field payroll-collaborator-select"
                  value={selectedPayrollUserId}
                  options={payrollUserOptions}
                  onChange={(value) => {
                    setSelectedPayrollUserId(value);
                    setEditingLine(null);
                    setLineDrafts(
                      Object.fromEntries(
                        BUCKET_ORDER.map((b) => [b, { description: '', amount: '' }]),
                      ) as Record<PayrollExpenseBucket, { description: string; amount: string }>,
                    );
                  }}
                />
              ) : null}
            </div>
          </div>
        ) : (
          <div className="payroll-competence-bar">
            <div className="payroll-competence-year">
              <PremiumSelect
                id="payroll-year"
                label="Ano"
                wrapperClassName="premium-select-field payroll-year-select"
                value={String(year)}
                options={yearPremiumOptions}
                onChange={(v) => setYear(parseInt(v, 10))}
              />
              <PremiumSelect
                id="payroll-cycle"
                label="Ciclos deste ano"
                wrapperClassName="premium-select-field payroll-cycle-select"
                value={activeCycleId ?? ''}
                options={cyclePremiumOptions}
                onChange={(value) => {
                  setActiveCycleId(value);
                  setEditingLine(null);
                }}
              />
              {canManagePayroll ? (
                <PremiumSelect
                  id="payroll-collaborator"
                  label="Colaborador"
                  wrapperClassName="premium-select-field payroll-collaborator-select"
                  value={selectedPayrollUserId}
                  options={payrollUserOptions}
                  onChange={(value) => {
                    setSelectedPayrollUserId(value);
                    setEditingLine(null);
                    setLineDrafts(
                      Object.fromEntries(
                        BUCKET_ORDER.map((b) => [b, { description: '', amount: '' }]),
                      ) as Record<PayrollExpenseBucket, { description: string; amount: string }>,
                    );
                  }}
                />
              ) : null}
            </div>
          </div>
        )}
      </section>

      {loading ? (
        <section className="finance-surface">
          <p>Carregando…</p>
        </section>
      ) : !sheet ? (
        <section className="finance-surface">
          <div className="finance-section-header">
            <div>
              <h2>Nenhuma folha para {activeCycle?.name ?? 'este ciclo'}</h2>
              <p>
                {canManagePayroll
                  ? 'Crie a folha deste ciclo para lançar salários, encargos, 13º, férias e outros. Os lançamentos espelhados no Financeiro usarão o mesmo ciclo.'
                  : 'Ainda não há folha cadastrada para este ciclo.'}
              </p>
            </div>
          </div>
          {canManagePayroll ? (
            <button
              type="button"
              className="finance-action-button"
              disabled={saving || !activeCycleId}
              onClick={() => setCreateSheetModalOpen(true)}
            >
              {saving ? 'Criando…' : 'Criar folha do ciclo'}
            </button>
          ) : null}
        </section>
      ) : (
        <>
          <section className="finance-surface">
            <div className="finance-section-header">
              <div>
                <span className="finance-section-kicker">
                  Folha {sheet.cycleName || sheet.referenceMonth}
                </span>
                <h2>{canManagePayroll ? 'Resumo por grupo' : 'Resumo do período'}</h2>
              </div>
              {canManagePayroll ? (
                <button
                  type="button"
                  className="finance-card-button is-danger"
                  disabled={saving}
                  onClick={() => setConfirmDeleteSheet(true)}
                >
                  Excluir folha inteira
                </button>
              ) : null}
            </div>
            {canManagePayroll ? (
              <div className="payroll-bucket-summary-grid">
                {BUCKET_ORDER.map((b) => (
                  <div key={b} className="finance-hero-card payroll-bucket-summary-card">
                    <span>{BUCKET_LABELS[b]}</span>
                    <strong>{formatCurrency(visibleTotalsByBucket[b] ?? 0)}</strong>
                  </div>
                ))}
              </div>
            ) : (
              <div className="payroll-bucket-summary-grid payroll-employee-summary-grid">
                <div className="finance-hero-card payroll-bucket-summary-card is-income">
                  <span>Receitas</span>
                  <strong>{formatCurrency(employeeEarningsTotal)}</strong>
                </div>
                <div className="finance-hero-card payroll-bucket-summary-card is-expense">
                  <span>Despesas</span>
                  <strong>{formatCurrency(employeeDiscountsTotal)}</strong>
                </div>
                <div className="finance-hero-card payroll-bucket-summary-card is-net">
                  <span>Líquido</span>
                  <strong>{formatCurrency(employeeNetTotal)}</strong>
                </div>
              </div>
            )}
          </section>

          <div className="payroll-buckets">
            {visibleBucketOrder.map((bucket) => (
              <section
                key={bucket}
                className={`payroll-bucket${
                  !canManagePayroll
                    ? PAYROLL_DISCOUNT_BUCKETS.includes(bucket)
                      ? ' payroll-bucket-discounts'
                      : ' payroll-bucket-earnings'
                    : ''
                }`}
              >
                {!canManagePayroll && bucket === PAYROLL_EARNING_BUCKETS[0] ? (
                  <div className="payroll-employee-group-header">
                    <span className="finance-section-kicker">Proventos</span>
                    <h3>Receitas</h3>
                  </div>
                ) : null}
                {!canManagePayroll && bucket === 'withholdings' ? (
                  <div className="payroll-employee-group-header">
                    <span className="finance-section-kicker">Descontos</span>
                    <h3>Despesas</h3>
                  </div>
                ) : null}
                <div className="payroll-bucket-header">
                  <h3>{canManagePayroll ? BUCKET_LABELS[bucket] : EMPLOYEE_BUCKET_LABELS[bucket]}</h3>
                  <span className="payroll-bucket-total">
                    Subtotal: {formatCurrency(visibleTotalsByBucket[bucket] ?? 0)}
                  </span>
                </div>

                <table className="payroll-table">
                  <thead>
                    <tr>
                      <th>Descrição</th>
                      <th>Valor</th>
                      {canManagePayroll ? <th style={{ width: 210 }}>Ações</th> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {linesByBucket(bucket).map((line) =>
                      canManagePayroll && editingLine?.id === line.id ? (
                        <tr key={line.id}>
                          <td className="payroll-edit-cell">
                            <input
                              className="premium-text-input"
                              value={editingLine.description}
                              onChange={(e) =>
                                setEditingLine((prev) =>
                                  prev ? { ...prev, description: e.target.value } : prev,
                                )
                              }
                            />
                          </td>
                          <td className="payroll-edit-cell payroll-edit-cell-narrow">
                            <input
                              className="premium-text-input"
                              inputMode="decimal"
                              value={editingLine.amount}
                              onChange={(e) =>
                                setEditingLine((prev) =>
                                  prev ? { ...prev, amount: e.target.value } : prev,
                                )
                              }
                            />
                          </td>
                          <td className="payroll-line-actions">
                            <button
                              type="button"
                              className="payroll-action-button is-save"
                              onClick={() => void handleSaveEditLine()}
                            >
                              Salvar
                            </button>
                            <button
                              type="button"
                              className="payroll-action-button is-cancel"
                              onClick={() => setEditingLine(null)}
                            >
                              Cancelar
                            </button>
                          </td>
                        </tr>
                      ) : (
                        <tr key={line.id}>
                          <td>{line.description}</td>
                          <td>{formatCurrency(line.amount)}</td>
                          {canManagePayroll ? (
                            <td className="payroll-line-actions">
                              <button
                                type="button"
                                className="payroll-action-button is-edit"
                                onClick={() =>
                                  setEditingLine({
                                    id: line.id,
                                    description: line.description,
                                    amount: String(line.amount),
                                  })
                                }
                              >
                                Editar
                              </button>
                              <button
                                type="button"
                                className="payroll-action-button is-delete"
                                onClick={() =>
                                  setConfirmDeleteLine({
                                    id: line.id,
                                    label: line.description,
                                  })
                                }
                              >
                                Excluir
                              </button>
                            </td>
                          ) : null}
                        </tr>
                      ),
                    )}
                  </tbody>
                </table>

                {canManagePayroll ? (
                  <div className="payroll-add-row">
                    <input
                      className="premium-text-input"
                      placeholder="Descrição da rubrica"
                      value={lineDrafts[bucket].description}
                      onChange={(e) =>
                        setLineDrafts((prev) => ({
                          ...prev,
                          [bucket]: { ...prev[bucket], description: e.target.value },
                        }))
                      }
                    />
                    <input
                      className="premium-text-input"
                      placeholder="Valor (R$)"
                      inputMode="decimal"
                      value={lineDrafts[bucket].amount}
                      onChange={(e) =>
                        setLineDrafts((prev) => ({
                          ...prev,
                          [bucket]: { ...prev[bucket], amount: e.target.value },
                        }))
                      }
                    />
                    <button
                      type="button"
                      className="finance-action-button"
                      disabled={saving || (canManagePayroll && !selectedPayrollUserId)}
                      onClick={() => void handleAddLine(bucket)}
                    >
                      Adicionar
                    </button>
                  </div>
                ) : null}
              </section>
            ))}
          </div>

          {canDownloadPayslip ? (
            <section className="finance-surface payroll-payslip-panel">
              <div>
                <span className="finance-section-kicker">Contracheque individual</span>
                <h2>Baixar contracheque</h2>
                <p>
                  Gere o PDF do colaborador selecionado nesta folha. O arquivo usa as rubricas e
                  descontos vinculados a ele no ciclo atual.
                </p>
              </div>
              <button
                type="button"
                className="payroll-payslip-button"
                disabled={!selectedPayslipLine || !selectedPayrollUser}
                onClick={() =>
                  selectedPayslipLine && selectedPayrollUser
                    ? void handleDownloadPayslip(selectedPayslipLine.id, selectedPayrollUser.name)
                    : undefined
                }
              >
                Baixar contracheque
              </button>
            </section>
          ) : null}
        </>
      )}

      {canManagePayroll && createSheetModalOpen && (
        <ModalPortal>
        <div
          className="confirm-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="payroll-create-title"
          onClick={saving ? undefined : () => setCreateSheetModalOpen(false)}
        >
          <div
            className="confirm-modal payroll-create-sheet-dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="confirm-modal-header">
              <div className="confirm-modal-icon payroll-create-icon" aria-hidden="true">
                ⧉
              </div>
              <h3 id="payroll-create-title">Criar folha — {activeCycle?.name ?? 'ciclo'}</h3>
              <button
                type="button"
                className="confirm-modal-close"
                onClick={() => !saving && setCreateSheetModalOpen(false)}
                disabled={saving}
                aria-label="Fechar"
              >
                ×
              </button>
            </div>
            <div className="confirm-modal-body">
              <p>
                Deseja carregar esta folha com os <strong>mesmos dados</strong> da folha do ciclo{' '}
                <strong>{previousCycleLabel}</strong> (ciclo anterior)?
              </p>
              <p style={{ marginTop: 12, fontSize: '0.88rem', color: '#64748b' }}>
                Você pode copiar as linhas agora ou começar em branco e lançar manualmente.
              </p>
            </div>
            <div className="confirm-modal-footer payroll-create-sheet-footer">
              <button
                type="button"
                className="confirm-modal-button is-muted"
                disabled={saving}
                onClick={() => !saving && setCreateSheetModalOpen(false)}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="confirm-modal-button is-muted"
                disabled={saving}
                onClick={() => void submitCreateSheet(false)}
              >
                {saving ? 'Criando…' : 'Não, em branco'}
              </button>
              <button
                type="button"
                className="confirm-modal-button is-primary"
                disabled={saving}
                onClick={() => void submitCreateSheet(true)}
              >
                {saving ? 'Criando…' : 'Sim, copiar mês anterior'}
              </button>
            </div>
          </div>
        </div>
        </ModalPortal>
      )}

      <AlertModal
        isOpen={alert.isOpen}
        message={alert.message}
        type={alert.type}
        onClose={() => setAlert((a) => ({ ...a, isOpen: false }))}
      />

      <ConfirmModal
        isOpen={canManagePayroll && Boolean(confirmDeleteLine)}
        title="Excluir linha"
        message={
          confirmDeleteLine
            ? `Remover “${confirmDeleteLine.label}” desta folha?`
            : ''
        }
        isLoading={saving}
        onClose={() => setConfirmDeleteLine(null)}
        onConfirm={() => void handleDeleteLine()}
      />

      <ConfirmModal
        isOpen={canManagePayroll && confirmDeleteSheet}
        title="Excluir folha"
        message="Todas as linhas deste mês serão apagadas. Deseja continuar?"
        isLoading={saving}
        onClose={() => setConfirmDeleteSheet(false)}
        onConfirm={() => void handleDeleteSheet()}
      />
    </div>
  );
};

export default Payroll;
